<template>
  <form method="post" @submit.prevent="handleFormSubmit">
    <h5 class="modalEditProfile-content__header position-relative">
      Edit Profile
      <img
        class="close-btn"
        src="~@/assets/media/icons/close.svg"
        alt="close icon"
        @click="$emit('closeModal')"
      />
    </h5>

    <div class="row">
      <div class="col-md-6">
        <div class="form-group form-material">
          <div class="form-control text-muted">
            {{ form.email }}
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="form-group form-material">
          <input
            type="text"
            name="displayName"
            class="form-control"
            v-model.trim="form.displayName"
            v-validate="'max:12|regex:^[a-zA-Z0-9 ]*$'"
            data-vv-as="display name"
            placeholder="Display Name"
            @focus="onFocus($event)"
            @blur="onBlur($event)"
          />
        </div>
      </div>
      <div class="col-md-6">
        <div class="form-group form-material">
          <input
            type="text"
            name="firstName"
            class="form-control"
            v-model="form.firstName"
            v-validate="'required'"
            data-vv-as="first name"
            placeholder="First Name"
            @focus="onFocus($event)"
            @blur="onBlur($event)"
          />
        </div>
      </div>
      <div class="col-md-6">
        <div class="form-group form-material">
          <input
            type="text"
            name="lastName"
            class="form-control"
            v-model="form.lastName"
            v-validate="'required'"
            data-vv-as="last name"
            placeholder="Last Name"
            @focus="onFocus($event)"
            @blur="onBlur($event)"
          />
        </div>
      </div>
      <div class="col-md-6">
        <div class="form-group form-material">
          <input
            type="tel"
            name="mobile"
            class="form-control"
            v-model="form.mobile"
            v-validate="'numeric'"
            data-vv-as="mobile number"
            placeholder="Mobile Number"
            @focus="onFocus($event)"
            @blur="onBlur($event)"
          />
        </div>
      </div>
      <div v-for="(meta, index) in UserMeta" :key="index" class="col-lg-6">
        <div class="form-group form-material">
          <input
            type="text"
            :name="`meta[${meta.key}]`"
            class="form-control"
            v-model="meta.value"
            :placeholder="meta.label"
            @focus="onFocus($event)"
            @blur="onBlur($event)"
          />
        </div>
      </div>
    </div>
    <div
      class="modalEditProfile-buttons align-items-center row justify-content-between"
    >
      <div class="col-md-6">
        <a
          href="javascript:;"
          :style="{ color: primary }"
          @click="togglePasswordForm"
        >
          Change Password
        </a>
      </div>
      <div class="col-md-6 d-flex justify-content-end">
        <ActionButton
          class="mt-10"
          mode="outline"
          :theme="{ primary, secondary, event, danger }"
          @customEvent="$emit('closeModal')"
          label="Cancel"
        />
        <ActionButton
          class="mt-10"
          type="submit"
          :theme="{ primary, secondary, event, danger }"
          :disabled="isLoading"
          label="Update"
        />
      </div>
    </div>
  </form>
</template>

<script>
import { uniqBy, filter, unionBy, startCase } from "lodash";
import { get as getCookie } from "js-cookie";
import { mapActions } from "vuex";
import axios from "@/helpers/base";
import types from "@/store/auth/types";

const ActionButton = () => import("@/components/ActionButton");

export default {
  name: "UserProfileForm",
  components: {
    ActionButton
  },
  props: {
    togglePasswordForm: {
      type: Function,
      default: function() {}
    }
  },
  data() {
    return {
      isLoading: false
    };
  },
  computed: {
    primary() {
      return this.$store.state.app.scheme.primary;
    },
    secondary() {
      return this.$store.state.app.scheme.secondary;
    },
    event() {
      return this.$store.state.app.scheme.event;
    },
    danger() {
      return this.$store.state.app.scheme.danger;
    },
    form() {
      return { ...this.$store.getters.getUser };
    },
    UserMeta() {
      const { UserMeta, id } = this.form;
      const formMeta = UserMeta.map(i => ({ ...i, userId: id }));
      const metaItem = (key, value = null, label, userId) => ({
        key,
        value,
        label: label || startCase(key),
        userId: userId || id
      });
      const defaultMeta = [
        metaItem("company"),
        metaItem("position"),
        metaItem("location"),
        metaItem("website")
      ];
      let unioned = unionBy(formMeta, defaultMeta, "key");
      return uniqBy(
        filter(unioned, val => {
          return !val.key.includes("owner:company");
        }),
        "key"
      );
    }
  },
  methods: {
    ...mapActions([types.AUTH_SET]),
    validateState(ref) {
      if (
        this.veeFields[ref] &&
        (this.veeFields[ref].dirty || this.veeFields[ref].validated)
      ) {
        return !this.errors.has(ref);
      }

      return null;
    },
    async handleMetaUpdate() {
      try {
        const results = axios.post(
          `/users/${this.form.id}/meta`,
          this.UserMeta
        );

        return results;
      } catch (error) {
        this.$toastr.error(error.message, "Oops!");
      }
    },
    async handleFormSubmit() {
      this.isLoading = true;
      try {
        const valid = await this.$validator.validateAll();
        if (!valid) {
          if (isNaN(this.form.mobile)) {
            throw Error("Please input valid phone number");
          } else {
            throw Error("Please fill required fields");
          }
        }

        await this.handleMetaUpdate();
        const result = await axios.patch(`/users/${this.form.id}`, {
          ...this.form,
          UserMeta: this.UserMeta
        });

        if (result.status !== 200) {
          throw Error("Unable to process request. Try again.");
        }

        await this[types.AUTH_SET](getCookie("jwt"));
        this.$toastr.success("Successfully updated account info.", "Saved");
        this.$emit("closeModal");
      } catch (error) {
        this.$toastr.error(error.message, "Oops!");
      }

      this.isLoading = false;
    },
    onFocus(event) {
      let field = event.target;
      field.style.boxShadow = `0 2px 0 ${this.primary}`;
    },
    onBlur(event) {
      let field = event.target;
      field.style.boxShadow = null;
    }
  }
};
</script>

<style lang="scss" scoped>
.close-btn {
  cursor: pointer;
  position: absolute;
  right: 15px;
  top: 0;
}
.modalEditProfile-buttons {
  .btn {
    @media (max-width: 767px) {
      padding: 8px 14px;
    }
  }
}
.form-group {
  &.form-material > .form-control {
    border-bottom: 1px solid $gray-200;
    & + span {
      display: block;
      height: 2px;
      opacity: 0;
      transition: all 0.2s linear;
      width: 100%;
    }
    &:focus {
      border-bottom: "2px solid black";
      box-shadow: 0 2px 0 transparent;
      & + span {
        opacity: 1;
      }
    }
  }
}
</style>
